import React from 'react';
import * as Yup from 'yup';
import { RaisedButton, GhostButton } from '@hobs/components/Buttons';
import { Formik, Field, Form } from 'formik';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next/src/index';
import Icon from '@hobs/components/Icon';
import Input from '@hobs/components/Input';
import InputLabel from '@hobs/components/InputLabel';
import DateInput from '@hobs/components/DateInput';
import Switch from '@hobs/components/Switch';
import DropDown from '@hobs/components/Dropdown';
import { format } from '@hobs/utils/formatWithTimeZone';
import { dateToInitialMonth } from '@hobs/utils/dateToInitialMonth';
import { DATE_FORMAT, PHASE_TYPES } from '@hobs/utils/constants';
import { phoneRegex } from '@hobs/validations';
import Styles from './SorForm.module.scss';

const SorForm = props => {
  const { t, disabled, sorPhase } = props;

  function removeNull(data) {
    for (const key in data) {
      if (data.hasOwnProperty(key)) {
        if (null === data[key]) data[key] = '';
      }
    }
    return data;
  }

  const sorUnitSchema = Yup.object().shape({
    email: Yup.string().email(t('Invalid email format')),
    phone: Yup.string()
      .matches(phoneRegex, {
        message: t('Please enter a valid phone number'),
        excludeEmptyString: true
      })
      .min(6, t('Phone number cannot be shorter than 6 symbols'))
      .max(100, t('Phone number cannot be longer than 100 symbols'))
      .nullable(),
    invoiceAddressName: Yup.string().when(['isPhaseFsorOrHigher'], {
      is: true,
      then: Yup.string()
        .required(t(t('Name is required')))
        .max(100, t('Name must be at most 100 characters')),
      otherwise: Yup.string().max(100, t('Name must be at most 100 characters'))
    }),
    invoiceAddressAddress1: Yup.string().when(['isPhaseFsorOrHigher'], {
      is: true,
      then: Yup.string()
        .required(t('Address 1 is required'))
        .max(100, t('Address 1 must be at most 100 characters')),
      otherwise: Yup.string().max(
        100,
        t('Address 1 must be at most 100 characters')
      )
    }),
    invoiceAddressAddress2: Yup.string().when(['isPhaseFsorOrHigher'], {
      is: true,
      then: Yup.string()
        .required(t('Address 2 is required'))
        .max(100, t('Address 2 must be at most 100 characters')),
      otherwise: Yup.string().max(
        100,
        t('Address 2 must be at most 100 characters')
      )
    }),
    invoiceAddressAddress3: Yup.string().max(
      100,
      t('Address 3 must be at most 100 characters')
    ),
    invoiceAddressSendingNationReference: Yup.string().max(
      100,
      t('Sending Nation Reference must be at most 100 characters')
    ),
    invoiceAddressCountry: Yup.string().when(['isPhaseFsorOrHigher'], {
      is: true,
      then: Yup.string()
        .required(t('Country is required'))
        .max(100, t('Country must be at most 100 characters')),
      otherwise: Yup.string().max(
        100,
        t('Country must be at most 100 characters')
      )
    }),
    financePocName: Yup.string().when(['isPhaseFsorOrHigher'], {
      is: true,
      then: Yup.string()
        .required(t('Name is required'))
        .max(100, t('Name must be at most 100 characters')),
      otherwise: Yup.string().max(100, t('Name must be at most 100 characters'))
    }),
    financePocEmail: Yup.string()
      .email(t('invalid email format'))
      .max(100, t('Email must be at most 100 characters')),
    financePocPhone: Yup.string().when(['isPhaseFsorOrHigher'], {
      is: true,
      then: Yup.string()
        .matches(phoneRegex, {
          message: t('Please enter a valid phone number'),
          excludeEmptyString: true
        })
        .required(t('Phone is required'))
        .min(6, t('Phone number cannot be shorter than 6 symbols'))
        .max(100, t('Phone must be at most 100 characters')),
      otherwise: Yup.string()
        .matches(phoneRegex, {
          message: t('Please enter a valid phone number'),
          excludeEmptyString: true
        })
        .min(6, t('Phone number cannot be shorter than 6 symbols'))
        .max(100, t('Phone must be at most 100 characters'))
    })
  });

  const isPhaseFsorOrHigher = phase => {
    return (
      phase.toUpperCase() == PHASE_TYPES.FSOR.name.toUpperCase() ||
      phase.toUpperCase() == PHASE_TYPES.Execution.name.toUpperCase() ||
      phase.toUpperCase() == PHASE_TYPES.Billing.name.toUpperCase() ||
      phase.toUpperCase() == PHASE_TYPES.Finished.name.toUpperCase()
    );
  };

  const initialValues = Object.assign(
    {},
    {
      creationDate: '',
      isActive: false,
      userId: '',
      email: '',
      phone: '',
      likelyFrom: '',
      likelyTo: '',
      invoiceAddressName: '',
      invoiceAddressAddress1: '',
      invoiceAddressAddress2: '',
      invoiceAddressAddress3: '',
      invoiceAddressSendingNationReference: '',
      invoiceAddressCountry: '',
      financePocName: '',
      financePocEmail: '',
      financePocPhone: '',
      isPhaseFsorOrHigher: isPhaseFsorOrHigher(sorPhase)
    },
    props.unit
  );

  return (
    <Formik
      validationSchema={sorUnitSchema}
      validateOnBlur={false}
      validateOnChange={false}
      enableReinitialize={true}
      initialValues={removeNull(initialValues)}
      onSubmit={props.updateUnit}
    >
      {({ setFieldValue, values, isSubmitting }) => (
        <Form className={Styles.form}>
          <div className={Styles.statusField}>
            <Field
              name="isActive"
              component={Switch}
              alignIndicator="right"
              setFieldValue={setFieldValue}
              checked={values.isActive}
              disabled={disabled}
            >
              {t(' Status:')}
            </Field>
            <InputLabel>
              {values.isActive ? t('Active') : t('Inactive')}
            </InputLabel>
          </div>
          <div className={Styles.twoColumns}>
            <div>
              <strong>{t('Responsible person')}</strong>
              <Field
                label={t('Name/Rank')}
                name="userId"
                component={DropDown}
                onChange={e => {
                  setFieldValue('userId', e.target.value);
                  const person = props.responsiblePerson.find(
                    p => p.userId === e.target.value
                  );
                  if (person) {
                    setFieldValue('email', person.email);
                    setFieldValue('phone', person.phone || '');
                  } else {
                    setFieldValue('email', '');
                    setFieldValue('phone', '');
                  }
                }}
                disabled={disabled}
              >
                <option value="">{t('Choose responsible person')}</option>
                {props.responsiblePerson.map(person => (
                  <option key={person.userId} value={person.userId}>
                    {person.name}
                  </option>
                ))}
              </Field>

              <Field name="email" component={Input} disabled={disabled}>
                {t('Email')}
              </Field>
              <Field name="phone" component={Input} disabled={disabled}>
                {t('Tel.')}
              </Field>
            </div>
            <div>
              <strong>{t('Initiation details')}</strong>
              <div className={Styles.textField}>
                <InputLabel>{t('Date of creation')}</InputLabel>
                <div>
                  {values.creationDate &&
                    format(values.creationDate, DATE_FORMAT)}
                  &nbsp;
                </div>
              </div>
              <Field
                name="likelyFrom"
                setFieldValue={setFieldValue}
                component={DateInput}
                disabled={disabled}
              >
                {t('Participation likely from')}
              </Field>
              <Field
                name="likelyTo"
                initialMonth={dateToInitialMonth(
                  values.likelyFrom,
                  values.likelyTo
                )}
                setFieldValue={setFieldValue}
                component={DateInput}
                disabled={disabled}
              >
                {t('Participation likely to')}
              </Field>
            </div>
            <div>
              <strong>{t('Invoice address')}</strong>
              <Field
                name="invoiceAddressName"
                component={Input}
                disabled={disabled}
              >
                {t('Name')}
              </Field>
              <Field
                name="invoiceAddressAddress1"
                component={Input}
                disabled={disabled}
              >
                {t('Address 1')}
              </Field>
              <Field
                name="invoiceAddressAddress2"
                component={Input}
                disabled={disabled}
              >
                {t('Address 2')}
              </Field>
              <Field
                name="invoiceAddressAddress3"
                component={Input}
                disabled={disabled}
              >
                {t('Address 3')}
              </Field>
              <Field
                name="invoiceAddressSendingNationReference"
                component={Input}
                disabled={disabled}
              >
                {t('Sending Nation Reference')}
              </Field>
              <Field
                name="invoiceAddressCountry"
                component={Input}
                disabled={disabled}
              >
                {t('Country')}
              </Field>
            </div>
            <div>
              <strong>{t('Finance POC')}</strong>
              <Field
                name="financePocName"
                component={Input}
                disabled={disabled}
              >
                {t('Name')}
              </Field>
              <Field
                name="financePocEmail"
                component={Input}
                disabled={disabled}
              >
                {t('Email')}
              </Field>
              <Field
                name="financePocPhone"
                component={Input}
                disabled={disabled}
              >
                {t('Phone')}
              </Field>
            </div>
          </div>
          <div className={Styles.buttons}>
            {!disabled && (
              <>
                <RaisedButton type="submit" disabled={isSubmitting}>
                  {t('Save')}
                </RaisedButton>
                {values.unitId && (
                  <GhostButton
                    type="button"
                    iconLeft={<Icon icon="Close" />}
                    onClick={props.removeUnit}
                  >
                    {t('Remove from exercise')}
                  </GhostButton>
                )}
              </>
            )}
          </div>
        </Form>
      )}
    </Formik>
  );
};
SorForm.propTypes = {
  t: PropTypes.func,
  unit: PropTypes.object,
  updateUnit: PropTypes.func,
  removeUnit: PropTypes.func,
  responsiblePerson: PropTypes.arrayOf(PropTypes.object),
  disabled: PropTypes.bool,
  sorPhase: PropTypes.string
};

export default withTranslation()(SorForm);
